import { useCallback, useState } from 'react'
import {
    useFindOneStockPersonalEquiposMutation,
    useFindStockPersonalEquiposAuxMutation
} from '../../SliceLogistica'
import { StockPersonalENTITY, StockPersonalEquiposENTITY } from 'sigo-entities'
import { useSelector } from 'react-redux'
import { RootState } from '../../AdapterStore'
import { indexedDB } from '../../useIndexedDB'

interface Params {
    StockPersonal: StockPersonalENTITY
}

export const useStockPersonalEquiposRepository = () => {
    const { user } = useSelector((state: RootState) => state.auth)

    const [_findOneStockPersonalEquipos, { isError, error }] = useFindOneStockPersonalEquiposMutation()
    const [_findStockPersonalEquipos, { isError: isErrorFindStock, error: errorFindStock }] = useFindStockPersonalEquiposAuxMutation()

    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState<StockPersonalEquiposENTITY[]>([])
    const [dataEquiposPersonal, setDataEquiposPersonal] = useState<StockPersonalEquiposENTITY>(new StockPersonalEquiposENTITY())

    const findStockPersonalEquipos = useCallback(async ({ pipeline }: { pipeline: any[] }) => {
        setIsLoading(true)
        try {
            // ONLINE
            if (navigator.onLine) {
                const response = await _findStockPersonalEquipos({
                    pais: user.Pais.Codigo,
                    delegacion: user.Delegacion.Codigo,
                    pipeline
                }).unwrap()
                const now = Date.now()

                await indexedDB.stock_personal_equipos.clear()
                await indexedDB.stock_personal_equipos.bulkPut(
                    response.map(e => ({
                        ...e,
                        pendingSync: 0,
                        syncedAt: now,
                        updatedAt: now
                    }))
                )

                setData(response)
                return response
            }

            // OFFLINE
            const localData = await indexedDB.stock_personal_equipos.toArray()
            setData(localData)
            return localData
        } finally {
            setIsLoading(false)
        }
    }, [_findStockPersonalEquipos])

    const findOneStockPersonalEquipos = useCallback(async ({ StockPersonal }: Params) => {
        setIsLoading(true)
        try {
            // ONLINE
            if (navigator.onLine) {
                const pipeline = [
                    {
                        $match: {
                            CodigoLlave: StockPersonal.CodigoLlave,
                            CodigoBusqueda: StockPersonal.CodigoBusqueda,
                            Identificacion: StockPersonal.Personal.Identificacion,
                            ID_StockPersonal: StockPersonal.ID_StockPersonal,
                        }
                    },
                    {
                        $addFields: {
                            Equipos: {
                                $filter: {
                                    input: '$Equipos',
                                    as: 'equipo',
                                    cond: { $eq: ['$$equipo.Estado.ID_Estado', 2] }
                                }
                            }
                        }
                    }
                ]
                const response = await _findOneStockPersonalEquipos({
                    pais: user.Pais.Codigo,
                    delegacion: user.Delegacion.Codigo,
                    pipeline
                }).unwrap()
                // Se cachea para que quede disponible offline tras la primera
                // consulta exitosa del día (antes solo se cacheaba vía el
                // prefetch masivo de la pantalla de Logística, que el flujo de
                // Somacyl nunca dispara).
                const now = Date.now()
                await indexedDB.stock_personal_equipos.put({
                    ...response,
                    pendingSync: 0,
                    syncedAt: now,
                    updatedAt: now
                })
                setDataEquiposPersonal(response)
                return response
            }
            // OFFLINE
            const localData = await indexedDB.stock_personal_equipos.toArray()
            const response = localData.filter(item =>
                item.ID_StockPersonal === StockPersonal.ID_StockPersonal
                && item.CodigoBusqueda === StockPersonal.CodigoBusqueda
                && item.CodigoLlave === StockPersonal.CodigoLlave)

            if (response?.length !== 1) {
                throw new Error('No se pudo obtener los equipos para el stock seleccionado')
            }
            response[0].Equipos = response[0].Equipos.filter((e) => e.Estado.ID_Estado === 2)
            setDataEquiposPersonal(response[0])
            return response[0]
        } finally {
            setIsLoading(false)
        }
    }, [user, _findOneStockPersonalEquipos])

    return {
        data,
        isLoading,
        findOneStockPersonalEquipos,
        isError: isError || isErrorFindStock,
        error: error || errorFindStock,
        dataEquiposPersonal,
        findStockPersonalEquipos
    }
}